import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchDishById,
  addIngredientToDish ,
  getCountries,
  addProductComponents,
  addRecipeComponents,
  fetchDishes
} from "../../api/api_dishes";

// Async thunks
export const fetchDishByIdThunk = createAsyncThunk(
  "dishes/fetchDishById",
  async (dishId) => {
    const response = await fetchDishById(dishId);
    return response.data;
  }
);

export const fetchDishesListThunk = createAsyncThunk(
  "dishes/fetchDishesList",
  async (params = {}) => {
    const response = await fetchDishes(params);
    return response.data;
  }
);

/** Menyer list — all affiliate dishes, paginated (separate from Dashboard cache) */
export const MENYER_DISHES_PER_PAGE = 30;

export const fetchMenyerDishesListThunk = createAsyncThunk(
  "dishes/fetchMenyerDishesList",
  async ({ page = 1, per_page = MENYER_DISHES_PER_PAGE } = {}) => {
    const response = await fetchDishes({
      page,
      per_page,
    });
    return response.data;
  }
);

export const addIngredientToDishThunk = createAsyncThunk(
  "dishes/addIngredient",
  async ({ dishId, ingredients }) => {
    const response = await addIngredientToDish(dishId, ingredients);
    return response.data;
  }
);

export const addProductComponentsThunk = createAsyncThunk(
  "dishes/addProductComponents",
  async ({ dishId, components }) => {
    const response = await addProductComponents(dishId, components);
    return response.data;
  }
);

export const addRecipeComponentsThunk = createAsyncThunk(
  "dishes/addRecipeComponents",
  async ({ dishId, components }) => {
    const response = await addRecipeComponents(dishId, components);
    return response.data;
  }
);

export const getCountriesThunk = createAsyncThunk(
  "dishes/getCountries",
  async () => {
    const response = await getCountries();
    return response.data;
  }
);

const dishesSlice = createSlice({
  name: "dishes",
  initialState: {
    currentDish: null,
    dishesList: {
      data: [],
      pagination: null,
      lastFetched: null,
      isLoading: false,
      error: null
    },
    menyerDishesList: {
      data: [],
      pagination: null,
      page: 1,
      lastFetched: null,
      isLoading: false,
      error: null
    },
    availableComponents: [],
    countries: [],
    status: "idle",
    error: null,
  },
  reducers: {
    resetDishState: (state) => {
      state.currentDish = null;
      state.availableComponents = [];
      state.countries = [];
      state.status = "idle";
      state.error = null;
    },
    clearDishesListCache: (state) => {
      state.dishesList = {
        data: [],
        pagination: null,
        lastFetched: null,
        isLoading: false,
        error: null
      };
    },
    updateDishPortionSize: (state, action) => {
      if (state.currentDish) {
        state.currentDish.portion_size = action.payload;
      }
    },
    updateDishImage: (state, action) => {
      if (state.currentDish) {
        state.currentDish.image = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchDishesListThunk
      .addCase(fetchDishesListThunk.pending, (state) => {
        state.dishesList.isLoading = true;
        state.dishesList.error = null;
      })
      .addCase(fetchDishesListThunk.fulfilled, (state, action) => {
        state.dishesList.isLoading = false;
        state.dishesList.data = action.payload.data;
        state.dishesList.pagination = action.payload.pagination;
        state.dishesList.lastFetched = Date.now();
        state.dishesList.error = null;
      })
      .addCase(fetchDishesListThunk.rejected, (state, action) => {
        state.dishesList.isLoading = false;
        state.dishesList.error = action.error.message;
        // Set lastFetched to prevent immediate retry on error
        // Only set if it's not already set, to avoid overwriting successful fetches
        if (!state.dishesList.lastFetched) {
          state.dishesList.lastFetched = Date.now();
        }
      })
      .addCase(fetchMenyerDishesListThunk.pending, (state, action) => {
        state.menyerDishesList.isLoading = true;
        state.menyerDishesList.error = null;
        state.menyerDishesList.page = action.meta.arg?.page ?? 1;
      })
      .addCase(fetchMenyerDishesListThunk.fulfilled, (state, action) => {
        const requestedPage = action.meta.arg?.page ?? 1;
        if (requestedPage !== state.menyerDishesList.page) return;

        state.menyerDishesList.isLoading = false;
        state.menyerDishesList.data = action.payload.data;
        state.menyerDishesList.pagination = action.payload.pagination;
        state.menyerDishesList.lastFetched = Date.now();
        state.menyerDishesList.error = null;
      })
      .addCase(fetchMenyerDishesListThunk.rejected, (state, action) => {
        const requestedPage = action.meta.arg?.page ?? 1;
        if (requestedPage !== state.menyerDishesList.page) return;

        state.menyerDishesList.isLoading = false;
        state.menyerDishesList.error = action.error.message;
        if (!state.menyerDishesList.lastFetched && state.menyerDishesList.data.length === 0) {
          state.menyerDishesList.lastFetched = Date.now();
        }
      })
      // Handle fetchDishByIdThunk
      .addCase(fetchDishByIdThunk.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchDishByIdThunk.fulfilled, (state, action) => {
        state.status = "succeeded";
        // Transform the dish data to include components from ingredients
        const dish = action.payload.dish;
        if (dish && dish.ingredients) {
          // Map ingredients to components with full score fields preserved for averaging
          dish.components = dish.ingredients.map((ingredient) => {
            const connectionId = ingredient.id ?? ingredient.connection_id ?? ingredient.ingredient_id;
            const name = ingredient.name || ingredient.product_name || ingredient.dish_name || 'Komponent';
            const amount = ingredient.weight_in_amount ?? ingredient.amount ?? 0;
            const percentage = ingredient.weight_in_percentage ?? ingredient.percentage ?? null;
            const origin = ingredient.origin ?? null;
            const unitAbbr = ingredient.unit_abbr ?? ingredient.unit ?? null;

            const climate = ingredient.climate_score ?? null;
            const water = ingredient.water_score ?? null;
            const health = ingredient.health_score ?? null;
            const social = ingredient.social_justice_score ?? null;

            const sustainability_values = (climate ?? water ?? health ?? social) != null
              ? { climate, water, health, social }
              : undefined;

            return {
              ...ingredient,
              id: connectionId,
              connection_id: connectionId,
              name,
              amount,
              percentage,
              origin,
              unit_abbr: unitAbbr,
              sustainability_values,
              source_type: ingredient.source_type || ingredient.type,
              sourceType: ingredient.source_type || ingredient.type,
            };
          });
        }
        state.currentDish = dish;
        state.availableComponents = action.payload.components || [];
      })
      .addCase(fetchDishByIdThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Handle getCountriesThunk
      .addCase(getCountriesThunk.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getCountriesThunk.fulfilled, (state, action) => {
        state.status = "succeeded";
        console.log("Countries response:", action.payload);
        // The API returns the countries directly, so we store them as-is
        state.countries = action.payload;
        console.log("Stored countries:", state.countries);
      })
      .addCase(getCountriesThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Handle addIngredientToDishThunk
      .addCase(addIngredientToDishThunk.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addIngredientToDishThunk.fulfilled, (state, action) => {
        if (action.payload.error) {
          state.error = action.payload.error;
        } else {
          state.status = "succeeded";
          // Update the dish with the new component
          if (state.currentDish) {
            // Transform the new ingredient to component format, preserving all score fields
            const newComponent = {
              ...action.payload,
              id: action.payload.id,
              connection_id: action.payload.id,
              name: action.payload.name || 'Unknown Ingredient',
              amount: action.payload.weight_in_amount,
              percentage: action.payload.weight_in_percentage,
              origin: action.payload.origin_country_id,
              unit_abbr: action.payload.unit_abbr,
              sustainability_values: {
                climate: action.payload.climate_score,
                water: action.payload.water_score,
                health: action.payload.health_score,
                social: action.payload.social_justice_score
              }
            };
            state.currentDish.components = [
              ...state.currentDish.components,
              newComponent,
            ];
          }
        }
      })
      .addCase(addIngredientToDishThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Add product components
      .addCase(addProductComponentsThunk.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addProductComponentsThunk.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(addProductComponentsThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Add recipe components
      .addCase(addRecipeComponentsThunk.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addRecipeComponentsThunk.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(addRecipeComponentsThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});

export const { resetDishState, clearDishesListCache, updateDishPortionSize, updateDishImage } = dishesSlice.actions;
export default dishesSlice.reducer; 